/**
 * El lado de la pagina del service worker (SPEC 15.7): lo registra, se entera de
 * cuando hay una version nueva esperando, y recarga UNICAMENTE cuando el usuario
 * lo pide.
 *
 * La regla que ordena todo este archivo: nunca se recarga solo. Una recarga
 * automatica se lleva por delante medio formulario de alta, y el alta de esta
 * aplicacion tiene notas, leccion y emociones escritas a mano.
 */

type Listener = (ready: boolean) => void;

const listeners = new Set<Listener>();

let waiting: ServiceWorker | null = null;

/**
 * `controllerchange` tambien salta en la primera instalacion, cuando el service
 * worker recien activado toma el control. Sin esta bandera, la primera visita de
 * cada usuario terminaria en una recarga que nadie pidio.
 */
let reloadRequested = false;

export function registerServiceWorker(): void {
  // Solo en produccion. En desarrollo Vite sirve los modulos sin hash y un
  // service worker cacheando por encima vuelve loco el ciclo de trabajo.
  if (!import.meta.env.PROD) return;

  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!reloadRequested) return;

    window.location.reload();
  });

  // Despues de `load`: el service worker compite por el ancho de banda con la
  // primera pintura, y la primera pintura va antes.
  window.addEventListener("load", () => {
    void install();
  });
}

/** Se suscribe al aviso de version nueva y devuelve como darse de baja. */
export function subscribeToUpdates(listener: Listener): () => void {
  listeners.add(listener);
  listener(waiting !== null);

  return () => {
    listeners.delete(listener);
  };
}

export function applyUpdate(): void {
  reloadRequested = true;

  if (waiting === null) {
    window.location.reload();

    return;
  }

  // El service worker nuevo esta instalado pero en espera. Este mensaje es lo
  // unico que le hace tomar el relevo, y de ahi sale el `controllerchange` que
  // recarga la pagina.
  waiting.postMessage({ type: "SKIP_WAITING" });
}

async function install(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.register("/sw.js");

    watch(registration);
  } catch {
    // Sin service worker la aplicacion funciona igual; lo que se pierde es el
    // modo sin conexion, y eso no vale una pantalla de error.
  }
}

function watch(registration: ServiceWorkerRegistration): void {
  // Puede haber uno esperando de una visita anterior.
  announce(registration.waiting);

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;

    if (installing === null) return;

    installing.addEventListener("statechange", () => {
      if (installing.state === "installed") announce(installing);
    });
  });
}

function announce(candidate: ServiceWorker | null): void {
  if (candidate === null) return;

  // Sin controlador es la PRIMERA instalacion, no una actualizacion: avisar de
  // una "version nueva" al que acaba de abrir la aplicacion por primera vez es
  // mentirle.
  if (navigator.serviceWorker.controller === null) return;

  waiting = candidate;

  for (const listener of listeners) listener(true);
}
